/**
 * Die Worte der Gerätelisten in den Karten „Vespera Desktop (Mac)“ und „Chrome-Extension“. (07.10.2026, Plan „mehrere
 * Geräte“, Scheiben S4 und S6)
 *
 * Martin hat zwei Laptops mit Vespera Desktop und Chrome. Seit S1 (Macs) und S6 (Browser) bleiben alle verbunden;
 * Vespera arbeitet an genau EINEM Gerät je Art, dem aktiven. Die Karten zeigen sie als Liste. Hier stehen nur die
 * Sätze und Regeln (reine Funktionen, ohne Seite) — gezeichnet wird in frontend/lib/mac-geraete-liste.ts.
 *
 * ⚠️ EIN Satz je Aussage für beide Arten: Was Mac und Browser unterscheidet, steht in WORTE (der Parameter `art`,
 *    Vorgabe "mac"). Zwei Kopien desselben Satzes wären zwei Besitzer (Memory „Zwei Besitzer“).
 * ⚠️ Ehrlich (Memory „Anzeige behauptet zu viel“): „Vespera arbeitet hier“ NUR beim Gerät mit `aktiv: true`;
 *    „bereit“ nur, wenn verbunden, nicht pausiert und nicht gesperrt (Regel 1 der Wahl, server/geraete-wahl.ts);
 *    „gerade benutzt“ nur bei `benutzt === "gerade"`. Die Wahl-Zeile sagt nur, was der Server als Grund nennt —
 *    die Automatik (Regel 3/4) ist bis Scheibe S2 aus, darum steht dort heute nie „an dem du gerade sitzt“,
 *    solange der Server es nicht so begründet.
 */
import { desktopStand, erweiterungStand, type UpdateStand } from "../../shared/app-version.js";
import type { BrowserGeraet, DaemonStatus, GeraeteArt, MacGeraet, MacWahl } from "./connectors-api.js";
import { bedienungshilfenHinweis, istNeueDesktopApp, zeitWien } from "./desktop-verbinden.js";

/**
 * Ein Gerät der Liste. Die Form der Macs (server/daemon-bridge.ts GeraetAnzeige); ein Browser wird beim Eingang in
 * diese Form gebracht (browserAlsGeraet) — ohne Pause, Bedienungshilfen und „getrennt“, mit seinem Betriebssystem.
 */
export type Geraet = MacGeraet & { os?: string | null; aeltere_erweiterung?: boolean };

/** Die Browser-Form des Servers (GET /api/ext/status) → die Form der Liste. */
export function browserAlsGeraet(b: BrowserGeraet): Geraet {
  return {
    id: b.id,
    name: b.name,
    aktiv: b.aktiv,
    wahl: b.wahl,
    verbunden: b.verbunden,
    verbunden_seit: b.verbunden_seit,
    zuletzt: b.zuletzt,
    getrennt: false,
    version: b.version,
    bedienungshilfen: null,
    benutzt: b.benutzt,
    pausiert: false,
    aeltere_app: false,
    os: b.os,
    aeltere_erweiterung: b.aeltere_erweiterung,
  };
}

/** Was Mac und Browser in den Sätzen unterscheidet. */
interface ArtWorte {
  ein: string;
  mehrere: string;
  /** „am MacBook Pro“ / „in Chrome (macOS)“ */
  bei: (name: string) => string;
  /** „an welchem Mac“ / „in welchem Browser“ */
  welchem: string;
  /** „an welchem“ / „in welchem“ (ohne Hauptwort) */
  welchemKurz: string;
  /** „an einem anderen Mac“ / „in einem anderen Browser“ */
  anderem: string;
  /** „am Mac“ / „im Browser“ */
  imGeraet: string;
  /** „zu den Macs“ / „zu den Browsern“ */
  zuDen: string;
  /** Warum gerade keines bereit ist. */
  keinesWeil: string;
  /** Wann die Hand-Wahl endet (außer: er wählt anders, 4 Uhr). */
  endet: string;
}

const WORTE: Readonly<Record<GeraeteArt, ArtWorte>> = {
  mac: {
    ein: "Mac",
    mehrere: "Macs",
    bei: (n) => `am ${n}`,
    welchem: "an welchem Mac",
    welchemKurz: "an welchem",
    anderem: "an einem anderen Mac",
    imGeraet: "am Mac",
    zuDen: "zu den Macs",
    keinesWeil: "pausiert oder Bildschirm gesperrt",
    endet: "bis er gesperrt oder weg ist",
  },
  browser: {
    ein: "Browser",
    mehrere: "Browser",
    bei: (n) => `in ${n}`,
    welchem: "in welchem Browser",
    welchemKurz: "in welchem",
    anderem: "in einem anderen Browser",
    imGeraet: "im Browser",
    zuDen: "zu den Browsern",
    keinesWeil: "der Rechner ist gesperrt",
    endet: "bis sein Rechner gesperrt oder er weg ist",
  },
};

/** Das Hauptwort einer Art: „Mac“ / „Browser“. */
export const geraetWort = (art: GeraeteArt = "mac"): string => WORTE[art].ein;

/** Höchstens so viele Geräte je Art und Konto (server/daemon-bridge.ts GERAETE_HOECHSTENS, extension-hub.ts BROWSER_HOECHSTENS). */
export const MAC_HOECHSTENS = 4;

export const vierHinweis = (art: GeraeteArt = "mac"): string => `Mehr als vier ${WORTE[art].mehrere} gehen nicht – trenne zuerst einen.`;
export const VIER_MACS_HINWEIS = vierHinweis("mac");

/**
 * Ältere App (vor 1.1.0, ohne Gerätekennung): der Server kennt sie nur über ihren Schlüssel, im Speicher.
 * Wahl und Trennen gelten bis zum Neustart von Vespera (server/daemon-bridge.ts handSetzen, _gesperrtImSpeicher).
 * Einen Namen setzt der Server bei JEDER neuen Verbindung zurück (namenFestlegen, Zweig `v.alt` — Schlaf oder WLAN
 * reichen): darum gibt es dort kein „Umbenennen“ (zeilenAktionen), und der Hinweis sagt, ab wann es geht.
 * ⚠️ Martins beide Macs sind ältere Apps, bis 1.1.0 draußen ist — das ist sein erster Eindruck der Liste.
 */
export const AELTERE_APP_HINWEIS =
  "Ältere App: Wahl und Trennen gelten bis zum nächsten Neustart von Vespera; den Namen kannst du ab Version 1.1.0 selbst wählen.";

/**
 * Ältere Erweiterung (vor 2.2.0): Sie meldet weder Betriebssystem noch, ob der Rechner gesperrt ist. Name und Wahl
 * halten trotzdem (jeder Browser hat seine eigene Kopplung, gespeichert) — anders als bei der älteren Mac-App gilt
 * hier also NICHT „bis zum Neustart“. Was fehlt, ist nur die Sperre.
 */
export const AELTERE_ERWEITERUNG_HINWEIS =
  "Ältere Erweiterung: Vespera sieht nicht, ob dieser Rechner gesperrt ist – das kann erst Version 2.2.0.";

const GETRENNT_HINWEIS = "Verbindet sich erst wieder, wenn du diesen Mac neu verbindest.";

/**
 * Die Rückfrage beim Trennen. Eine ältere App sperrt der Server nur im Speicher — nach dem nächsten Neustart von
 * Vespera kommt sie von selbst wieder (server/daemon-bridge.ts geraetAktion, Zweig trennen). Ein Browser: Der Server
 * löscht seine Kopplung (server/extension-hub.ts trenneKopplung) — er braucht einen neuen Code.
 */
export function trennenFrage(g: Geraet, art: GeraeteArt = "mac"): string {
  if (art === "browser") return "Der Browser verbindet sich erst wieder, wenn du ihn mit einem neuen Code koppelst.";
  return g.aeltere_app
    ? "Der Mac bleibt getrennt, bis du ihn neu verbindest oder Vespera das nächste Mal neu startet."
    : "Der Mac verbindet sich erst wieder, wenn du ihn neu verbindest.";
}

/** Dieselbe Regel wie der Server (server/daemon-bridge.ts geraetName) und die Mac-App (geraet.py name_bereinigen). */
export const NAME_REGEL = "Ein Name hat 1 bis 40 Zeichen: Buchstaben, Ziffern, Leerzeichen und . - '";
const NAME_MUSTER = /^[\p{L}\p{N} .\-']{1,40}$/u;

/**
 * Den Namen so bereinigen wie der Server — oder null, wenn er nicht passt. NFC zuerst: ein „ä“ aus zwei Zeichen
 * (a + Trema, etwa aus einer kopierten Datei) bestünde die Regel sonst nicht (das Trema ist kein Buchstabe).
 */
export function bereinigterName(roh: string): string | null {
  const s = roh.normalize("NFC").replace(/[’‘`´]/g, "'").replace(/\s+/g, " ").trim();
  return NAME_MUSTER.test(s) ? s : null;
}

type GeraetArt = "arbeitet" | "bereit" | "pausiert" | "gesperrt" | "nicht-verbunden" | "getrennt";

/** In welchem Zustand ist das Gerät? Eine Stelle für Wort, Punkt und Knöpfe. */
export function geraetArt(g: Geraet): GeraetArt {
  if (!g.verbunden) return g.getrennt ? "getrennt" : "nicht-verbunden";
  if (g.aktiv) return "arbeitet";
  if (g.pausiert) return "pausiert";
  if (g.benutzt === "gesperrt") return "gesperrt";
  return "bereit";
}

const ZUSTAND_WORT: Record<GeraetArt, string> = {
  arbeitet: "Vespera arbeitet hier",
  bereit: "bereit",
  pausiert: "pausiert",
  gesperrt: "Bildschirm gesperrt",
  "nicht-verbunden": "nicht verbunden",
  getrennt: "getrennt",
};

export function zustandWort(g: Geraet): string {
  return ZUSTAND_WORT[geraetArt(g)];
}

/**
 * Die ruhige Zeile unter dem Namen: „verbunden seit 17:33 · gerade benutzt“ oder „zuletzt da 05.10. 16:49“.
 * Wiener Zeit. Was der Server nicht sauber meldet, fehlt — nie eine rohe Angabe.
 */
export function geraetDetail(g: Geraet, jetzt: Date = new Date()): string {
  const teile: string[] = [];
  if (g.verbunden) {
    const seit = zeitWien(g.verbunden_seit, jetzt);
    if (seit) teile.push(`verbunden seit ${seit}`);
    if (g.benutzt === "gerade") teile.push("gerade benutzt");
    else if (typeof g.benutzt === "string" && /^vor [0-9]{1,4} min$/.test(g.benutzt)) teile.push(`${g.benutzt} benutzt`);
  } else {
    const zuletzt = zeitWien(g.zuletzt, jetzt);
    if (zuletzt) teile.push(`zuletzt da ${zuletzt}`);
  }
  return teile.join(" · ");
}

/** Die Versionszeile je Gerät — dieselben Sätze wie bisher (shared/app-version.ts), nur für verbundene. */
export function geraetVersion(
  g: Geraet,
  s: Pick<DaemonStatus, "aktuell" | "download" | "downloadVersion">,
  art: GeraeteArt = "mac",
): UpdateStand | null {
  if (!g.verbunden) return null;
  if (art === "browser") return erweiterungStand({ verbunden: true, bei: g.version, aktuell: s.aktuell ?? null });
  return desktopStand({
    verbunden: true,
    bei: g.version,
    aktuell: s.aktuell ?? null,
    download: !!s.download,
    downloadVersion: s.downloadVersion ?? null,
  });
}

/** Ruhige Hinweise an der Zeile (ältere App/Erweiterung, getrennt) und — rot — fehlende Bedienungshilfen. */
export function geraetHinweise(g: Geraet): { notizen: string[]; warnung: string | null } {
  const notizen: string[] = [];
  if (g.getrennt) notizen.push(GETRENNT_HINWEIS);
  if (g.aeltere_app) notizen.push(AELTERE_APP_HINWEIS);
  if (g.verbunden && g.aeltere_erweiterung) notizen.push(AELTERE_ERWEITERUNG_HINWEIS);
  const warnung = g.verbunden && g.bedienungshilfen === false ? bedienungshilfenHinweis(istNeueDesktopApp(g.version)) : null;
  return { notizen, warnung };
}

export type ZeilenAktion = "waehlen" | "umbenennen" | "trennen";

/**
 * Welche Knöpfe die Zeile trägt.
 *  - „Hier arbeiten“ nur, wo die Wahl auch greift: verbunden, nicht aktiv, nicht pausiert, nicht gesperrt (eine
 *    Wahl auf ein gesperrtes Gerät endet sofort, server/geraete-wahl.ts handAbgelaufen).
 *  - „Umbenennen“ nicht bei einer älteren Mac-App (der Server setzt ihren Namen bei jeder Verbindung zurück).
 *  - „Trennen“ nicht bei einem schon getrennten.
 */
export function zeilenAktionen(g: Geraet): ZeilenAktion[] {
  const a: ZeilenAktion[] = [];
  if (geraetArt(g) === "bereit") a.push("waehlen");
  if (!g.aeltere_app) a.push("umbenennen");
  if (!g.getrennt) a.push("trennen");
  return a;
}

export const AKTION_WORT: Record<ZeilenAktion, string> = {
  waehlen: "Hier arbeiten",
  umbenennen: "Umbenennen",
  trennen: "Trennen",
};

interface WahlZeile {
  /** Die kleine Überschrift: „Automatisch“ oder „Von dir gewählt“. */
  titel: string;
  satz: string;
  /** Knopf „Wieder automatisch“. */
  zurueck: boolean;
}

/**
 * Die Zeile über der Liste: wer wählt das Gerät, und warum dieses. Null, wenn es nichts zu sagen gibt — keines
 * verbunden (dann steht die Anleitung da) oder genau eines ohne Hand-Wahl (es gibt nichts zu wählen).
 *
 * Das von Hand gewählte Gerät nennt der Server in `wahl.hand` — auch, wenn die Wahl ruht oder wartet. Ist der
 * gewählte Mac pausiert, wechselt der Server NICHT still zum anderen (`grund: "nicht-bereit"`, kein aktiver Mac):
 * Vespera fragt nach — genau das sagt die Zeile. Ohne `wahl.hand` (älterer Server) bleibt sie ohne Namen; sie rät nicht.
 */
export function wahlZeile(geraete: readonly Geraet[], wahl: MacWahl | null | undefined, art: GeraeteArt = "mac"): WahlZeile | null {
  const w = WORTE[art];
  const verbunden = geraete.filter((g) => g.verbunden);
  const aktiv = verbunden.find((g) => g.aktiv) ?? null;
  const hand = wahl?.modus === "hand";
  if (hand) {
    let satz: string;
    // Eine ältere App meldet keine Sperre, und ihre Wahl hält nur bis zum Neustart von Vespera (nur im Speicher).
    if (aktiv?.wahl === "hand" && aktiv.aeltere_app) {
      satz = `Vespera arbeitet am ${aktiv.name}, bis du anders wählst oder er weg ist – längstens bis 4 Uhr früh oder bis Vespera neu startet.`;
    } else if (aktiv?.wahl === "hand" && aktiv.aeltere_erweiterung) {
      // Eine ältere Erweiterung meldet keine Sperre — die Wahl endet dort nicht beim Sperren (aber sie ist gespeichert).
      satz = `Vespera arbeitet ${w.bei(aktiv.name)}, bis du anders wählst oder er weg ist – längstens bis 4 Uhr früh.`;
    } else if (aktiv?.wahl === "hand") satz = `Vespera arbeitet ${w.bei(aktiv.name)}, ${w.endet} – längstens bis 4 Uhr früh.`;
    else {
      const gewaehlt = wahl?.hand ? geraete.find((g) => g.id === wahl.hand?.id) : undefined;
      const name = wahl?.hand?.name ?? gewaehlt?.name ?? null;
      if (wahl?.grund === "warten") {
        satz = name ? `Gewählt: ${name}. Vespera wartet kurz, bis er sich wieder meldet.` : `Vespera wartet kurz auf den ${w.ein}, den du gewählt hast.`;
      } else {
        const wie = gewaehlt?.pausiert ? "pausiert" : "nicht bereit";
        const wer = name ? `Gewählt: ${name} – er ist gerade ${wie}.` : `Der gewählte ${w.ein} ist gerade ${wie}.`;
        satz = aktiv ? `${wer} Bis dahin arbeitet Vespera ${w.bei(aktiv.name)}.` : `${wer} Vespera fragt nach, ${w.welchem} sie arbeiten soll.`;
      }
    }
    return { titel: "Von dir gewählt", satz, zurueck: true };
  }
  if (verbunden.length === 0) return null;
  if (verbunden.length === 1 && wahl?.grund !== "keins") return null;
  const grund = wahl?.grund ?? "";
  let satz: string;
  if (grund === "unklar") satz = `Mehrere ${w.mehrere} sind bereit. Vespera fragt nach, ${w.welchemKurz} sie arbeiten soll – oder du wählst hier.`;
  else if (grund === "keins") satz = `Gerade ist kein ${w.ein} bereit – ${w.keinesWeil}.`;
  else if (grund === "einziges") satz = `Nur ein ${w.ein} ist bereit – dort arbeitet Vespera.`;
  else if ((grund === "automatik" || grund === "zuletzt-benutzt") && aktiv) satz = `Vespera arbeitet ${w.imGeraet}, an dem du gerade sitzt.`;
  else if (grund === "arbeit" && aktiv) satz = `Vespera bleibt ${w.bei(aktiv.name)}, solange sie dort arbeitet.`;
  else if (grund === "bleibt" && aktiv) satz = `Vespera bleibt ${w.bei(aktiv.name)}, bis du ${w.anderem} weiterarbeitest.`;
  else if (aktiv) satz = `Vespera arbeitet ${w.bei(aktiv.name)}.`;
  else satz = `Vespera wählt den ${w.ein} selbst.`;
  return { titel: "Automatisch", satz, zurueck: false };
}

/**
 * Namen in NFC, an EINER Stelle für alles, was die Liste zeigt und vergleicht (Zeile, Wahl-Zeile, Meldungen,
 * Umbenennen): „Mäc“ aus a + Trema sieht sonst gleich aus, ist aber ein anderer Text.
 */
export function inNfc<T extends { geraete: readonly Geraet[]; wahl: MacWahl | null }>(s: T): T {
  const hand = s.wahl?.hand;
  return {
    ...s,
    geraete: s.geraete.map((g) => ({ ...g, name: g.name.normalize("NFC") })),
    wahl: s.wahl && hand ? { ...s.wahl, hand: { ...hand, name: hand.name?.normalize("NFC") ?? null } } : s.wahl,
  };
}

/** Die Fehler der Aktionen in Worten — nie ein nackter Code, nie eine Zahl. */
export function aktionFehlerText(status: number | null, code: string | null, name: string, art: GeraeteArt = "mac"): string {
  const w = WORTE[art];
  if (status === null) return "Keine Verbindung zum Server – bitte gleich noch einmal versuchen.";
  if (status === 401) return "Du bist nicht mehr angemeldet. Bitte die Seite neu laden.";
  if (status === 404 || code === "geraet-unbekannt") return `Vespera kennt ${name} nicht mehr – die Liste ist neu geladen.`;
  if (code === "geraet-nicht-verbunden") return `${name} ist gerade nicht verbunden – dort kann Vespera nicht arbeiten.`;
  if (code === "name-vergeben") return `Diesen Namen trägt schon ein anderer ${w.ein}.`;
  if (code === "name-ungueltig") return NAME_REGEL;
  if (status === 503) return `Die Verbindung ${w.zuDen} ist gerade nicht bereit – bitte gleich noch einmal versuchen.`;
  if (status === 400) return "Das ging nicht – bitte die Seite neu laden und noch einmal versuchen.";
  return "Das ging gerade nicht – bitte gleich noch einmal versuchen.";
}

/**
 * Was nach einer gelungenen Aktion angesagt wird — aus der ANTWORT abgeleitet, nicht aus dem Knopf: Ein gewähltes
 * Gerät, das gerade nicht bereit ist, wird nicht „aktiv“ genannt; und hat die Wahl am Server gar nicht gehalten
 * (`wahl.modus` wieder „automatisch“, z. B. weil das Gerät inzwischen gesperrt ist), heißt es nicht „Deine Wahl gilt“
 * (Prüfung 07.10.).
 */
export function erfolgText(
  aktion: "waehlen" | "umbenennen" | "trennen" | "automatik",
  id: string | null,
  name: string,
  neu: { geraete: readonly Geraet[]; wahl: MacWahl | null },
  art: GeraeteArt = "mac",
): string {
  const w = WORTE[art];
  const g = id ? neu.geraete.find((x) => x.id === id) : undefined;
  if (aktion === "automatik") return `Vespera wählt den ${w.ein} wieder selbst.`;
  if (aktion === "waehlen") {
    if (g?.aktiv) return `Vespera arbeitet jetzt ${w.bei(g.name)}.`;
    if (neu.wahl?.modus === "hand") return `Deine Wahl gilt – ${name} ist aber gerade nicht bereit.`;
    return `${name} ist gerade nicht bereit – die Wahl gilt nicht, Vespera wählt weiter selbst.`;
  }
  if (aktion === "umbenennen") return g ? `Der ${w.ein} heißt jetzt ${g.name}.` : "Umbenannt.";
  return !g?.verbunden ? `${name} ist getrennt.` : `${name} wird getrennt.`;
}

/**
 * Hat sich seit `vorher` (dem Stand beim Erzeugen des Codes) ein Gerät NEU verbunden? Dann geht der Kopplungscode weg.
 * Neu heißt: eine Kennung, die vorher NICHT verbunden war (ein weiteres Gerät, oder ein getrenntes, das wiederkommt).
 * ⚠️ Ein Gerät, das schon verbunden war und sich nur neu anmeldet (Schlaf, WLAN, Neustart der App), zählt nicht — sonst
 *    verschwand der Code, bevor das neue Gerät ihn hatte (Prüfung 07.10.).
 * Ohne Geräteliste (älterer Server): die alte Regel — verbunden, und vorher nicht oder mit anderer Version.
 */
type VerbindungsStand = { connected: boolean; version?: string | null; geraete?: readonly { id: string; verbunden: boolean }[] };
export function neueVerbindung(vorher: VerbindungsStand | null, jetzt: VerbindungsStand): boolean {
  const liste: unknown = jetzt.geraete;
  if (Array.isArray(liste)) {
    const schonDa = new Set((vorher?.geraete ?? []).filter((g) => g.verbunden).map((g) => g.id));
    return (liste as NonNullable<VerbindungsStand["geraete"]>).some((g) => g.verbunden && !schonDa.has(g.id));
  }
  return jetzt.connected && (!vorher?.connected || (jetzt.version ?? null) !== (vorher.version ?? null));
}
